import { BlackjackCardPosition, BlackjackHandValues, BlackjackSidePosition } from './blackjack.model';
import { GameType } from '../../../models/communication/app-enums.model';
import { TableUpdate } from '../../../models/communication/app-state.model';
import { HandValueKey } from '../../../models/communication/games.model';
import { GameBase } from '../../game-base';
import { BlackjackGameFlow } from '../../game-flows/blackjack.game.flow';
import { GameConfig } from '../../models/game-config.model';
import { CardState, SideState } from '../../models/game-view.model';

export type BlackjackTableUpdate = Partial<BlackjackHandValues> & TableUpdate & { dealerBlackjack?: boolean; dealerBusted?: boolean };

const blackjackHandValue = 21;
const blackjackCardCount = 2;
const pendingCardStates = [CardState.Checked, CardState.MessageFaceDown, CardState.MessageFaceUp];

export const blackjackConfig: GameConfig = {
    sides: [
        {
            position: BlackjackSidePosition.Dealer,
            hands: [
                {
                    label: $localize`DEALER`,
                    handValueKey: 'dealerHandValue' as HandValueKey,
                    cards: [
                        { position: BlackjackCardPosition.UpCard },
                        { position: BlackjackCardPosition.HoleCard },
                        { position: BlackjackCardPosition.DrawCard1, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard2, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard3, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard4, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard5, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard6, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard7, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard8, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard9, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard10, state: CardState.Hidden },
                        { position: BlackjackCardPosition.DrawCard11, state: CardState.Hidden }
                    ]
                }
            ]
        }
    ]
};

export class BlackjackGame extends GameBase {
    static override config = blackjackConfig;
    static override gameType = GameType.Blackjack;
    override flow: BlackjackGameFlow = new BlackjackGameFlow(this);

    // The table sends no outcome, so the result comes from the dealer hand: the card event's flags
    // live, or the recorded total in history.
    override handleOutcome(table: BlackjackTableUpdate, handValue?: number): void {
        const hand = this.getFirstHandInSide(BlackjackSidePosition.Dealer);

        // No result while a dealer card is still to come: the hole card is down or a draw is prompted.
        if (hand.cards.some((card) => pendingCardStates.includes(card.state))) {
            return;
        }

        const value = table.dealerHandValue ?? handValue ?? hand.amount.value;
        const shownCards = hand.cards.filter((card) => card.state === CardState.Show).length;

        if (table.dealerBusted ?? value > blackjackHandValue) {
            this.updateTitle($localize`DEALER BUSTS`);
        } else if (table.dealerBlackjack ?? (value === blackjackHandValue && shownCards === blackjackCardCount)) {
            this.updateTitle($localize`DEALER BLACKJACK`);
        } else {
            this.updateTitle($localize`DEALER STANDS ON ${value}`);
        }

        this.updateSides(SideState.Default);
    }

    override getFieldLabel(): string {
        return $localize`DEALER FIELD`;
    }

    override getOutcomeLabel(): string {
        // Never called: see handleOutcome.
        return '';
    }

    override setOutcomeStates(): void {
        // Never called: see handleOutcome.
    }
}
